<template>
  <div class="project-list">
    <!-- 项目列表（企业微信手机端，仅查看） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="项目名称/编号/提出人" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.projectType" :data="projectTypeOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="项目类型" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.priority" :data="priorityOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="优先级" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.projectStatus" :data="projectStatusOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="状态" @change="resetSearch"/>
      </div>
    </mobile-list-header>

    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList"
                 :refresh="resetSearch" ref="mobileList">
      <list-card class="project-item" v-for="item in dataList" :key="item.id" :label="item.projectName">
        <template #desc>
          <span v-if="item.priority" class="priority-tag" :class="'p-' + priorityTag(item.priority)">
            {{ item.priority }}
          </span>
          <span class="status-tag" :class="'tag-' + projectStatusTag(item.projectStatus)">
            {{ projectStatusLabel(item.projectStatus) || "—" }}
          </span>
        </template>
        <data-column label="项目编号">{{ item.projectCode || "—" }}</data-column>
        <data-column label="提出人">{{ item.sourceName || "—" }}</data-column>
        <data-column label="项目类型">{{ item.projectType || "—" }}</data-column>
        <data-column label="期望完成">{{ dateOnly(item.expectDate) }}</data-column>
        <data-column label="任务/批次">{{ item.taskCount || 0 }} / {{ item.sprintCount || 0 }}</data-column>
        <div v-if="item.description" class="description">{{ item.description }}</div>
      </list-card>
    </mobile-list>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import projectApi from "@/api/dz-project/project/projectApi";
import {PROJECT_STATUS_OPTIONS, projectStatusLabel, projectStatusTag} from "@/views/h5/crm/project/manage/projectStatus";

// 项目类型：新功能/优化/缺陷修复/技术改造；优先级：紧急/高/中/低（与 web 端一致）
const PROJECT_TYPE_LIST = ["新功能", "优化", "缺陷修复", "技术改造"];
const PRIORITY_LIST = ["紧急", "高", "中", "低"];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ProjectListQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileConditionSelect, MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        projectType: "",
        priority: "",
        projectStatus: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      projectTypeOptions: PROJECT_TYPE_LIST.map(t => ({value: t, label: t})),
      priorityOptions: PRIORITY_LIST.map(p => ({value: p, label: p})),
      projectStatusOptions: PROJECT_STATUS_OPTIONS,
    };
  },
  methods: {
    projectStatusLabel,
    projectStatusTag,
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      projectApi.queryList({
        ...this.cleanParams(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    cleanParams() {
      const params = {};
      ["searchKey", "projectType", "priority", "projectStatus"].forEach(key => {
        const value = this.searchInfo[key];
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });
      return params;
    },
    priorityTag(priority) {
      const map = {"紧急": "danger", "高": "warning", "中": "primary", "低": "info"};
      return map[priority] || "info";
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
  }
};
</script>

<style lang="scss" scoped>
.project-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.project-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .priority-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .p-danger{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .p-warning{
    color: var(--el-color-warning);
    background: var(--el-color-warning-light-9);
  }
  .p-primary{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .p-info{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
  }
  .tag-primary{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .tag-info{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
  }
  .tag-success{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
  .tag-warning{
    color: var(--el-color-warning);
    background: var(--el-color-warning-light-9);
  }
  .tag-danger{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .description{
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--yq-background-color);
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--yq-font-color-desc);
    line-height: 1.5;
  }
}
</style>
